import axios from 'axios';

import {
  AllocateAssetPayload,
  AllocateCurratedWallpaperPayload,
} from '../../../api/ui';
import API from '@wetransfer/transfer/api';
import {
  SUPPORTED_MIME_TYPES,
  SUPPORTED_IMAGE_TYPES,
} from '@wetransfer/transfer/constant/mime-types';
import request from '@wetransfer/transfer/modules/http/axios/request';
import {
  CustomResponseError,
  CustomResponse,
} from '@wetransfer/transfer/modules/http/axios/response';
import { State } from '@wetransfer/transfer/store/types';

// There is a similar type WallpaperRaw on app/javascript/store/types.ts but
// the endpoints returns different keys for them
export interface Wallpaper {
  id: number;
  source_url: string;
  wallpaper_type: string;
  asset_url: string;
  thumbnail_small: string;
  thumbnail_medium: string;
  thumbnail_large: string;
  staged: boolean;
  represented_class_name: 'Wallpaper';
}

export interface EmailBackground {
  id: number;
  image_url: string;
  source_url: string;
  thumbnail_url: string;
  staged: boolean;
  represented_class_name: 'EmailBackground';
}

export interface ProfilePicture {
  id: number;
  source_url: string;
  image_url: string;
  thumbnail_url: string;
  represented_class_name: 'ProfilePicture';
}

export interface ProjectLogo {
  id: string;
  source_url: string;
  image_url: string;
  url: string;
  represented_class_name: 'ProjectLogo';
}

export type UserUploadedAssetModel =
  | ProfilePicture
  | Wallpaper
  | EmailBackground
  | ProjectLogo;

export interface UserUploadedAssetResponse {
  presigned_put_url: string;
  content_type: string;
  model: UserUploadedAssetModel;
}

export interface HttpResponse<Body> {
  body: Body;
}

export interface CuratedWallPaper {
  imagePath: string;
  thumbnailLarge: string;
  credits: string;
  curated: boolean;
}

export const isCuratedWallpaper = (
  wallpaper: Wallpaper | CuratedWallPaper
): wallpaper is CuratedWallPaper => {
  return 'curated' in wallpaper && wallpaper.curated;
};

export const ASSET_MODEL_CLASS_MAPPING = {
  wallpaper: 'Wallpaper',
  profile_picture: 'ProfilePicture',
  email_background: 'EmailBackground',
} as const;

//The function API.UI.getPreviewUrls will return preview URLs only after Peek
//finishes processing the item.
function isModelWithAllPreviewUrlsSet(model: UserUploadedAssetModel): boolean {
  if (
    model.represented_class_name === 'Wallpaper' &&
    model.thumbnail_large &&
    model.thumbnail_medium &&
    model.thumbnail_small
  ) {
    return true;
  }

  if (model.represented_class_name === 'ProjectLogo' && model.url) {
    return true;
  }

  if (
    (model.represented_class_name === 'ProfilePicture' ||
      model.represented_class_name === 'EmailBackground') &&
    model.thumbnail_url
  ) {
    return true;
  }

  return false;
}

export default {
  cancelToken: null,

  allocateAsset(
    accountId: number,
    type: keyof typeof ASSET_MODEL_CLASS_MAPPING,
    filename: string
  ): Promise<HttpResponse<UserUploadedAssetResponse>> {
    const options = {
      asset_model_class: ASSET_MODEL_CLASS_MAPPING[type],
      account_id: accountId,
      original_asset_filename: filename,
    } as AllocateAssetPayload;

    return API.UI.allocateAsset(options) as Promise<
      HttpResponse<UserUploadedAssetResponse>
    >;
  },

  allocateCuratedWallpaper(
    accountId: number,
    filename: string
  ): Promise<HttpResponse<Wallpaper>> {
    const options = {
      account_id: accountId,
      image_path: filename,
    } as AllocateCurratedWallpaperPayload;

    return API.UI.allocateCuratedImage(options) as Promise<
      HttpResponse<Wallpaper>
    >;
  },

  uploadAsset(
    presignedUrl: string,
    contentType: string,
    file: File
  ): Promise<CustomResponseError | CustomResponse> {
    this.cancelToken = axios.CancelToken.source();

    return request({
      method: 'put',
      path: presignedUrl,
      params: file.slice(),
      cancelToken: this.cancelToken.token,
      headers: {
        'Content-Type': contentType,
      },
    });
  },

  cancelUpload(): void {
    if (this.cancelToken !== null) {
      this.cancelToken.cancel();
      this.cancelToken = null;
    }
  },

  isFileValid(
    type: keyof typeof ASSET_MODEL_CLASS_MAPPING,
    size: number,
    mimeType: string,
    assetUpload: State['session']['assetUpload']
  ): boolean {
    switch (type) {
      case 'wallpaper':
        return (
          size <= assetUpload.maxWallpaperSize &&
          SUPPORTED_MIME_TYPES.includes(mimeType)
        );
      case 'email_background':
        return (
          size <= assetUpload.maxEmailBackgroundSize &&
          SUPPORTED_IMAGE_TYPES.includes(mimeType)
        );
      case 'profile_picture':
        return (
          size <= assetUpload.maxProfilePictureSize &&
          SUPPORTED_IMAGE_TYPES.includes(mimeType)
        );
      default:
        return false;
    }
  },

  processPreviews(
    userUploadedAssetModel: UserUploadedAssetModel
  ): Promise<void> {
    const payload = {
      asset_model_id: userUploadedAssetModel.id,
      asset_model_class: userUploadedAssetModel.represented_class_name,
    };

    return API.UI.processPreviews(payload) as Promise<void>;
  },

  async getPreviewUrls(
    userUploadedAssetModel: UserUploadedAssetModel,
    attempts = 1,
    maxAttempts = 5
  ): Promise<UserUploadedAssetModel> {
    const response = (await API.UI.getPreviewUrls(
      {},
      {
        pathExtValues: {
          asset_model_id: userUploadedAssetModel.id,
          asset_model_class: userUploadedAssetModel.represented_class_name,
        },
      }
    )) as { body: UserUploadedAssetModel };
    const modelWithPreviewUrls = response.body;

    // The preview URLs will be all set only when Peek finishes the processing
    if (isModelWithAllPreviewUrlsSet(modelWithPreviewUrls)) {
      return modelWithPreviewUrls;
    }

    // Sometimes Peek needs more time to finish the processing
    if (attempts < maxAttempts) {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(
            this.getPreviewUrls(
              userUploadedAssetModel,
              attempts + 1,
              maxAttempts
            )
          );
        }, 1000 * attempts);
      });
    }

    throw new Error(
      'Could not get preview URLs from Peek because it has exceeded the max attempts'
    );
  },
};
